
import { Component, Prop, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import OriginVhostRepositoryInterface, {
  OriginVhostInterface,
  OriginVhostPHPModuleInterface,
} from '@/services/Vhost/Origin/Interfaces';
import { ServerInterface } from '@/services/Server/Interfaces';
import { Website } from '@/services/Website/interfaces';
import { SystemUserInterface } from '@/services/SystemUsers/interfaces';
import WildcardDialog from '@/components/dialogs/vhost/origin/WildcardDialog.vue';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'originVhostRepository',
  ],
  components: {
    WildcardDialog,
  },
})
export default class EditVhostDialog extends Vue {
  public set aliases(aliases: string[]) {
    this.data.vhost.aliases = aliases.map((alias) => ({ id: -1, alias }));
  }

  public get aliases(): string[] {
    return this.data.vhost.aliases.map((vhostAlias) => vhostAlias.alias);
  }

  @Prop() public data: {
    vhost: OriginVhostInterface,
    server: ServerInterface,
    website: Website,
    systemUser: SystemUserInterface
  };

  public visible: boolean = false;

  public originVhostRepository!: OriginVhostRepositoryInterface;
  public phpModules: OriginVhostPHPModuleInterface[] = [];
  public searchModule: string = '';

  public originVhostStatusStrings: { [key: number]: string } = {
    0: 'In Progress',
    1: 'Done',
    2: 'Error',
  };


  public wildcardDialog: string = '';
  public domainForWildcardDialog: string = '';
  public removeForWildcardDialog: boolean = false;

  public async createModule(): Promise<void> {
    if (!this.searchModule.trim()) {
      return;
    }

    try {
      const newModule = await this.originVhostRepository.createPHPModule(this.searchModule);
      this.data.vhost.php.modules = this.data.vhost.php.modules.filter((module) => module.id !== -1);
      this.phpModules = this.phpModules.filter((phpModule) => phpModule.id !== 1);
      this.data.vhost.php.modules.push(newModule);
      this.phpModules.push(newModule);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  public async getPHPModules(): Promise<void> {
    this.phpModules = await this.originVhostRepository.getPHPModules();
  }

  public set phpVersion(version: string) {
    this.data.vhost.php.version = Number(version.substr(3));
  }

  public get phpVersion(): string {
    return `php${this.data.vhost.php.version.toFixed(1)}`;
  }

  public saveChanges(): void {
    this.$emit('edit', this.data.vhost);
  }

  public async created(): Promise<void> {
    await this.getPHPModules();
    this.visible = true;
  }

  public destroyComponent(): void {
    this.$emit('close');
  }

  public async deleteLog(id: number): Promise<void> {
    try {
      await this.originVhostRepository.deleteLog(id);
      this.data.vhost.logs = this.data.vhost.logs.filter((log) => log.id !== id);
      this.$successDialog.show('Log deleted successfully.');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  public hasWildCard(): boolean {
    return this.aliases.includes(`*.${this.data.website.domain}`);
  }

  public showWildcardDialog(): void {
    this.removeForWildcardDialog = this.hasWildCard();
    this.domainForWildcardDialog = this.data.website.domain;
    this.wildcardDialog = 'wildcard-dialog';
  }

  public async createWildcard(remove: boolean): Promise<void> {
    try {
      if (!remove) {
        await this.originVhostRepository.createWildCard(this.data.vhost.id);
        this.$successDialog.show('Wildcard creation started.');
        return;
      }

      await this.originVhostRepository.removeWildCard(this.data.vhost.id);
      this.$successDialog.show('Wildcard remove started.');
      return;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message ? err.response.data.message : err.message);
    }
  }

  public validateAliases(): boolean[] {
    return [
      this.aliasesAreValid() || 'Invalid aliases!',
    ];
  }

  public aliasesAreValid(): boolean {
    return !this.aliases ||
        !this.aliases.length ||
        this.aliases.every((val) => val &&
            (
                val === `*.${this.data.website.domain}` ||
                /^(?!:\/\/)([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(val)
            ) &&
            val.length < 201,
        );
  }
}
